import { createApiServerFlow } from 'js/actions';
import { CREATE_API_SERVER_FLOW_REQUEST } from 'js/constants/actionTypes';
import { ROUTE_DATA_SOURCES, ROUTE_PIPELINES } from 'js/constants/pathnames';
import { FLOW_TYPE_CUSTOM, FLOW_TYPE_ELT } from 'js/constants/pipelineConstants';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { getCurrentProjectId } from 'js/reducers/app/selectors';
import flowTypes, { FLOW_CATEGORY } from 'new/components/flows/types/flowTypes';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { Button } from 'new/elements/Button';
import { ConnectionLoader } from 'new/elements/ConnectionLoader/ConnectionLoader';
import { flowDataMap, flowDataMapItems } from 'new/elements/flows/dataMap';
import { FlowTypeSelection } from 'new/elements/flows/FlowTypeSelection';
import { FlowType } from 'new/elements/flows/types';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { useAppContext } from 'new/hooks/useAppContext';
import { useIsFlowTypeEnabled } from 'new/hooks/useFlowTypeVisibility';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import { useHashedState } from 'new/state/hashedState/repository/useHashedState';
import { assignFlowToProjectIfPresent } from 'new/utils/projectAssignment';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import FlowInfo from './FlowInfo';
import classes from './FlowSelectionPage.module.scss';

const NEW_FLOW_URI = `${ROUTE_CONNECT}${ROUTE_DATA_SOURCES}/new`;

interface FlowSelectionPageProps {
  disableAutoBreadcrumbUpdate?: boolean;
  onSelection?: () => void;
}

export const FlowSelectionPage: FC<FlowSelectionPageProps> = ({
  disableAutoBreadcrumbUpdate = false,
  onSelection,
}) => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const currentProjectId = useSelector(getCurrentProjectId);

  const { setHeaderTitle, setCurrentSlug } = useAppContext();
  const [flowType, setFlowType] = useState<FlowType>('streaming');
  const isCreatingApiServerFlow = useIsLoading(CREATE_API_SERVER_FLOW_REQUEST);
  const data = flowDataMap[flowType];
  const inDevelopment = flowDataMapItems[FLOW_CATEGORY.coming_soon].includes(flowType);

  // Use centralized flow type visibility system
  const isCurrentFlowTypeEnabled = useIsFlowTypeEnabled(flowType);
  const eltFlowEnabled = useIsFlowTypeEnabled(FLOW_TYPE_ELT);
  // BYO V2 (this Studio/canvas shell rewrite) previously had no rollout flag
  // of its own — any org already allow-listed via the legacy org.features_enabled
  // "byo" check (isFlowTypeEnabled below) was dropped straight into it with no
  // separate opt-in, breaking the established per-rollout-gate practice.
  // Confirmed bug, fixed 2026-08-19 — see UI_ENABLE_BYO_V2 in featureFlags.ts.
  const isByoFlowTypeEnabled = useIsFlowTypeEnabled(FLOW_TYPE_CUSTOM);
  const byoV2Enabled = useFeatureFlag('UI_ENABLE_BYO_V2');

  const { save } = useHashedState('flows.selected');

  useEffect(() => {
    save(null);
  }, []);

  useEffect(() => {
    if (disableAutoBreadcrumbUpdate) return;
    setHeaderTitle('New Data Flow');
    setCurrentSlug('New Data Flow');
  }, [disableAutoBreadcrumbUpdate]);

  const handleOnClickNew = (selectedFlowType: string) => {
    onSelection?.();
    if (selectedFlowType === flowTypes.streaming) {
      history.push(NEW_FLOW_URI);
    } else if (selectedFlowType === FLOW_TYPE_ELT && !eltFlowEnabled) {
      // generate email to support@nexla.com and open mail client. also attach a subject
      window.location.href = 'mailto:support@nexla.com?subject=Request Access for ELT Flow';
    } else if (selectedFlowType === FLOW_TYPE_CUSTOM && !isByoFlowTypeEnabled) {
      // No BYO access at all (legacy org.features_enabled allowlist is off) —
      // request access, same as any other gated flow type.
      window.location.href = 'mailto:support@nexla.com?subject=Request Access for BYO Flow';
    } else if (selectedFlowType === FLOW_TYPE_CUSTOM && byoV2Enabled) {
      history.push(`${ROUTE_CONNECT}${ROUTE_PIPELINES}/custom/new`);
    } else if (selectedFlowType === FLOW_TYPE_CUSTOM) {
      // Org has BYO access but UI_ENABLE_BYO_V2 is off — this used to fall
      // through to the generic `else` branch below (same route every other
      // flow type without a dedicated create route uses) until this branch
      // added the two cases above and an unconditional custom check that
      // pre-empted it, silently turning off the one BYO creation path a
      // flag-off org actually had. Confirmed bug, fixed 2026-08-31 — restores
      // the legacy DirectFlow-based BYO wizard for flag-off orgs instead of a
      // dead-end "Request Access" mailto.
      history.push(`${ROUTE_CONNECT}${ROUTE_PIPELINES}/custom/source/new`);
    } else if (selectedFlowType === flowTypes.genai_rag) {
      history.push(`${ROUTE_CONNECT}${ROUTE_PIPELINES}/genai_rag/new`);
    } else if (selectedFlowType === flowTypes.api_server) {
      dispatch(createApiServerFlow()).then(async (response) => {
        const flowId = response.result?.flows?.id?.[0];
        if (flowId) {
          try {
            await assignFlowToProjectIfPresent({
              dispatch,
              flowNodeId: flowId,
              currentProjectId,
            });
          } catch (error) {
            console.error('Failed to assign flow to project:', error);
          }

          history.push(`${ROUTE_CONNECT}${ROUTE_PIPELINES}/api_server/${flowId}/edit`);
        }
      });
    } else if (selectedFlowType === FLOW_TYPE_CUSTOM) {
      history.push(`${ROUTE_CONNECT}${ROUTE_PIPELINES}/custom/new`);
    } else {
      history.push(`${ROUTE_CONNECT}${ROUTE_PIPELINES}/${selectedFlowType}/source/new`);
    }
  };

  const Image = data.ImageComponent;

  // Configuration for button based on flowType
  const flowTypeCTAConfig = useMemo(() => {
    if (inDevelopment) {
      return null;
    }

    // BYO's "Create" enablement is the legacy per-org allowlist alone —
    // UI_ENABLE_BYO_V2 only decides WHICH creation experience `handleOnClickNew`
    // routes to (v2 canvas vs. the legacy DirectFlow-based wizard), not whether
    // the org can create a BYO flow at all. Do not re-add byoV2Enabled here —
    // that previously made the button (and every flag-off org's only BYO
    // creation path) fall back to a dead-end "Request Access" mailto even
    // though a legacy creation route still exists and works. Confirmed bug,
    // fixed 2026-08-31.
    const isEnabled =
      flowType === FLOW_TYPE_ELT
        ? eltFlowEnabled
        : flowType === FLOW_TYPE_CUSTOM
          ? isByoFlowTypeEnabled
          : isCurrentFlowTypeEnabled;

    return {
      label: isEnabled ? 'Create' : 'Request Access',
      variant: isEnabled ? 'ghost' : 'outlined',
      color: isEnabled ? 'primary' : 'basic',
      analyticsEvent: `flow_${isEnabled ? 'create' : 'request_access'}_${flowType}`,
    };
  }, [flowType, isCurrentFlowTypeEnabled, eltFlowEnabled, isByoFlowTypeEnabled, inDevelopment]);

  const flowTypeCTA = flowTypeCTAConfig ? (
    <Button
      data-testid="new-flow-proceed"
      onClick={() => handleOnClickNew(flowType)}
      variant={flowTypeCTAConfig.variant as 'ghost' | 'outlined'}
      color={flowTypeCTAConfig.color as 'primary' | 'basic'}
      data-analytics={flowTypeCTAConfig.analyticsEvent}
    >
      {flowTypeCTAConfig.label}
    </Button>
  ) : null;

  return (
    <section className={classes.container}>
      {isCreatingApiServerFlow && (
        <ConnectionLoader loading message="Creating API Server Flow..." />
      )}
      <div className={classes.flowCards}>
        <FlowTypeSelection
          handleOnClickNew={handleOnClickNew}
          onFlowSelected={setFlowType}
          selected={flowType}
        />
      </div>
      <FlowInfo flowType={flowType} flowTypeCTA={flowTypeCTA} Image={Image} />
    </section>
  );
};
